/* eslint-disable react/no-danger */
import React, { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Cookies from "js-cookie";
import ReactTable from "components/lib/Global/ReactTable";
import TableActionMenu from "components/lib/Shared/TableActionMenu";
import {
  calculateCumulativeOwnership,
  delay,
  getFullName,
  getMaskedEmail,
  getBeneficialOwnershipFormFields,
  roundNumber,
} from "utils";
import { deleteBeneficialOwnerAsync } from "state/slices/tables/beneficialOwnersTableSlice";
import { useParams } from "react-router-dom";
import { CORPORATES, INDIVIDUALS } from "../SFLdata/config/constants";
import NotificationBanner from "components/lib/Shared/NotificationBanner";
import BeneficialOwnersSubRowAsync from "./BeneficialOwnersSubRowAsync";
import TypeOfControlColumn from "components/lib/Shared/TypeOfControlColumn";
import { PlusIcon } from "components/lib/Shared/Icons/sflIcons";
import { Constants } from "config/constants";
import AvatarIcon from "components/lib/Shared/Icons/AvatarIcon";
import {
  ArrowUpIcon,
  ArrowDownIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";

const BeneficialOwnersTable = ({
  handleEdit,
  handleDelete,
  data,
  showActionColumn,
  handleBackBtn,
  groupBOEntries = true,
  hideColumnsRow,
  isInUpdateCompanyDetailsContext,
  selectedTableType,
  parentsNeedingAttention = [],
}) => {
  const role = Cookies.get("role");
  const dispatch = useDispatch();
  const collapseBtnRef = useRef(null);
  const { company } = useSelector((state) => state.companySlice);
  const currentUser = useSelector((state) => state.userSlice);
  const params = useParams();
  const companyId = company?._id ?? params?.companyId;
  const { beneficialOwners, deleteBeneficialOwnerStatus } = useSelector(
    (state) => state.beneficialOwnersTableSlice
  );
  const beneficialOwnersDropdown = beneficialOwners.filter(
    (bo) => bo?.showInBoDropdown
  );
  const allBeneficialOwners = [
    ...beneficialOwners,
    ...beneficialOwnersDropdown,
  ];

  const deleteUnfoundLink = (row) => {
    if (!deleteBeneficialOwnerStatus.inProgressIds.includes(row._id)) {
      dispatch(
        deleteBeneficialOwnerAsync({
          beneficialOwnerId: row._id,
          companyId,
        })
      );
    }
  };

  const getBoFullName = ({ names, remainingMinorityShareholders }) => {
    if (remainingMinorityShareholders && remainingMinorityShareholders >= 0)
      return (
        <span>{`${remainingMinorityShareholders} Minority Shareholders`}</span>
      );
    return getFullName(names);
  };

  const getLinkedName = (row) => {
    const { linkToCompany, shareholderWithBO, JSCombinedName, idType } = row;

    if (JSCombinedName && !shareholderWithBO) {
      return JSCombinedName;
    }

    if (JSCombinedName && shareholderWithBO) {
      const linkedCompany = allBeneficialOwners.find(
        (bo) => bo._id === shareholderWithBO
      );
      if (!linkedCompany) {
        if (!deleteBeneficialOwnerStatus.inProgressIds.includes(row._id)) {
          dispatch(
            deleteBeneficialOwnerAsync({
              beneficialOwnerId: row._id,
              companyId,
            })
          );
        }
        return "";
      }

      if (linkedCompany.linkToCompany || linkedCompany.shareholderWithBO)
        return `${getLinkedName(linkedCompany)} -> ${getBoFullName(
          linkedCompany
        )} ${idType !== "Joint Shareholder" ? ` -> ${JSCombinedName}` : ""} `;

      return `${getBoFullName(linkedCompany)} ${
        idType !== "Joint Shareholder" ? ` -> ${JSCombinedName}` : ""
      }`;
    }

    if (linkToCompany) {
      const linkToCompanyBO = allBeneficialOwners.find(
        (bo) => bo._id === linkToCompany || bo._id === linkToCompany?._id
      );
      if (!linkToCompanyBO) {
        if (!deleteBeneficialOwnerStatus.inProgressIds.includes(row._id)) {
          dispatch(
            deleteBeneficialOwnerAsync({
              beneficialOwnerId: row._id,
              companyId,
            })
          );
        }
        return "";
      }
      if (linkToCompanyBO.linkToCompany || linkToCompanyBO.shareholderWithBO)
        return `${getLinkedName(linkToCompanyBO)} -> ${getBoFullName(
          linkToCompanyBO
        )}`;
      return getBoFullName(linkToCompanyBO);
    }

    if (shareholderWithBO) {
      const linkedCompany = allBeneficialOwners.find(
        (bo) => bo._id === shareholderWithBO
      );
      if (!linkedCompany) {
        if (!deleteBeneficialOwnerStatus.inProgressIds.includes(row._id)) {
          dispatch(
            deleteBeneficialOwnerAsync({
              beneficialOwnerId: row._id,
              companyId,
            })
          );
        }
        return "";
      }
      if (linkedCompany.linkToCompany || linkedCompany.shareholderWithBO)
        return `${getLinkedName(linkedCompany)} -> ${getBoFullName(
          linkedCompany
        )}`;
      return getBoFullName(linkedCompany);
    }
    return "Directly";
  };

  /**
   * Find parent info for a given BO
   */
  const getParentInfo = (bo) => {
    return parentsNeedingAttention.find((p) =>
      p.children.some((child) => child._id === bo._id)
    );
  };

  /**
   * Get change badge component
   */
  const getChangeBadge = (original) => {
    const hasChanges =
      original?._hasTransactionChanges || original?._isChangedParent;
    const isChild = original?._isChildOfChangedParent || original?._needsReview;
    const parentInfo = getParentInfo(original);

    if (hasChanges) {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
          <ArrowUpIcon className="w-3 h-3 mr-1" />
          Updated
        </span>
      );
    }

    if (isChild && parentInfo) {
      const isDecrease = parentInfo.isDecrease;
      return (
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            isDecrease
              ? "bg-orange-100 text-orange-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {isDecrease ? (
            <ArrowDownIcon className="w-3 h-3 mr-1" />
          ) : (
            <ArrowUpIcon className="w-3 h-3 mr-1" />
          )}
          {isDecrease ? "Reduce" : "Review"}
        </span>
      );
    }

    return null;
  };

  const columns = [
    {
      Header: "Details",
      accessor: "fullName",
      className: "p-4 text-ellipsis overflow-hidden",
      Cell: ({ row: { original } }) => {
        if (!groupBOEntries) return <div className="w-56"> </div>;
        if (original?.remainingMinorityShareholders) {
          const minorityFullName = `${original.remainingMinorityShareholders} Minority Shareholders`;
          return <span>{minorityFullName}</span>;
        }
        const emailAddress =
          original?.emailAddress || original?.electronicAddress?.emailAddress;
        const fullName = getBoFullName(original);

        const changeBadge = getChangeBadge(original);

        if (
          CORPORATES.includes(original.idType) ||
          original.idType === "Joint Shareholder"
        ) {
          return (
            <div className="flex items-center space-x-2">
              <span>{fullName}</span>
              {changeBadge}
            </div>
          );
        }
        return (
          <div className="flex items-center space-x-1 w-52">
            <div className={`flex-shrink-0`}>
              {original?.fileUploads?.passportPhoto ? (
                <img
                  className="w-8 h-8 rounded-full"
                  src={`${process.env.REACT_APP_IMAGE_BASE_URL}/images/${original?.fileUploads?.passportPhoto}`}
                  alt={"Bo Official"}
                />
              ) : (
                <div className="w-8 h-8 rounded-full">
                  <div className="relative w-8 h-8 overflow-hidden bg-gray-100 rounded-full dark:bg-gray-600">
                    <AvatarIcon />
                  </div>
                </div>
              )}
            </div>
            <div className="p-3 flex-1">
              <div className="flex items-center space-x-2">
                <h1 className="font-bold">{fullName}</h1>
                {changeBadge}
              </div>
              <p>
                {original?.type === "invited"
                  ? getMaskedEmail(emailAddress)
                  : emailAddress}
              </p>
              <p className="mt-1">{original.idType}</p>
            </div>
          </div>
        );
      },
    },
    {
      Header: "Link to Company",
      className: "p-4 text-ellipsis overflow-hidden max-w-[150px]",
      Cell: ({ row }) => {
        if (row.original?.LinkToCompanyLabel)
          return <div className="w-28">{row.original.LinkToCompanyLabel}</div>;
        if (row.original?.children) {
          const linkedCompanyNames = row.original.children?.map((child) =>
            getLinkedName(child)
          );
          if (linkedCompanyNames.length === 1)
            return <div className="w-28">{linkedCompanyNames}</div>;
          const lastName = linkedCompanyNames.pop();
          const linkedNames =
            linkedCompanyNames.join(", ") + " and " + lastName;
          return <div className="w-28">{linkedNames}</div>;
        }
        return <div className="w-28">{getLinkedName(row.original)}</div>;
      },
    },
    {
      Header: "Type Of Control",
      Cell: ({ row }) => {
        const fields = getBeneficialOwnershipFormFields({
          beneficialOwnershipForm: row.original.beneficialOwnershipForm,
          reviewTab: true,
          beneficialOwner: row.original,
          beneficialOwners,
        });

        return (
          <div>
            <TypeOfControlColumn fields={fields.slice(0, 4)} />
          </div>
        );
      },
    },
    {
      Header: "Cumulative Ownership",
      Cell: ({ row: { original } }) => {
        const { cumulativeShareholdings, cumulativeVotings } =
          calculateCumulativeOwnership(original, beneficialOwners);
        return (
          <div className="w-24">{`${roundNumber(
            cumulativeShareholdings
          )}%, ${roundNumber(cumulativeVotings)}%`}</div>
        );
      },
    },
    {
      Header: "Action",
      Cell: ({ row }) => {
        const isOwnerRow = [
          row.original?.electronicAddress?.emailAddress,
          row.original?.identificationDetails?.guardianEmailAddress,
        ].includes(currentUser.electronicAddress.emailAddress);

        const hasChanges =
          row.original?._hasTransactionChanges ||
          row.original?._isChangedParent ||
          (row.original?.children &&
            row.original.children.some(
              (child) => child._hasTransactionChanges
            ));
        const isChild =
          row.original?._isChildOfChangedParent ||
          row.original?._needsReview ||
          (row.original?.children &&
            row.original.children.some(
              (child) => child._needsReview || child._isChildOfChangedParent
            ));
        const isIndividualType = INDIVIDUALS.includes(row.original?.idType);
        const isCorporateType = CORPORATES.includes(row.original?.idType);

        const disableEditBtn =
          (row.original?.type === "invited" ||
            role === Constants.COOKIE_ROLES.OFFICIAL) &&
          !isOwnerRow;
        const parentOfficial = allBeneficialOwners.find(
          (bo) => bo && bo._id === row.original?.linkToCompany
        );
        const isParentComingFromCOTab =
          !parentOfficial?.linkToCompany && !parentOfficial?.shareholderWithBO;

        let reviewBtnProps = {};
        if (row?.getToggleRowExpandedProps) {
          reviewBtnProps = row?.getToggleRowExpandedProps();
        }

        // In UpdateCompanyDetails context with changes:
        // - Corporates with changes should open corporate dashboard
        // - Individuals with Review tag should expand SubRow for editing
        // - Individuals with Updated tag should only have View
        let hideEditBtnOverride = false;
        if (isInUpdateCompanyDetailsContext) {
          if (isIndividualType) {
            // Hide Edit for individuals with "Updated" tag (direct changes)
            hideEditBtnOverride = hasChanges && !isChild;
          }
          // Corporates always show Edit to allow managing children
        }

        return (
          <div className="flex flex-col items-center">
            {showActionColumn &&
              (groupBOEntries
                ? !row.isExpanded && row.original?.children
                : true) && (
                <TableActionMenu
                  row={row}
                  showViewBtn={role !== Constants.COOKIE_ROLES.OFFICIAL}
                  hideEditBtn={hideEditBtnOverride}
                  showReviewBtn={
                    !isInUpdateCompanyDetailsContext &&
                    !isOwnerRow &&
                    row.original?.type === "invited" &&
                    row.original.createdBy === currentUser._id &&
                    !row.original?.isReadOnly
                  }
                  disableEditBtn={
                    (((row.original?.type === "invited" ||
                      role === Constants.COOKIE_ROLES.OFFICIAL) &&
                      !isOwnerRow) ||
                      row.original?.isReadOnly) &&
                    !isParentComingFromCOTab &&
                    row.original?.isReadOnly
                  }
                  disableDeleteBtn={
                    isInUpdateCompanyDetailsContext ||
                    ((((row.original?.type === "invited" ||
                      role === Constants.COOKIE_ROLES.OFFICIAL) &&
                      !isOwnerRow) ||
                      row.original?.isReadOnly) &&
                      !isParentComingFromCOTab &&
                      row.original?.isReadOnly)
                  }
                  handleEdit={(row) => {
                    const isCorporateShareholder =
                      row.original.shareholderWithBO && !row.original.JSGroupID;
                    const isJointShareholder =
                      row.original.shareholderWithBO &&
                      row.original.JSGroupID &&
                      !row.original.linkToCompany;
                    const needsCompanyOfficialsRedirect =
                      !row.original.linkToCompany &&
                      !isInUpdateCompanyDetailsContext;

                    // Corporate with changes in UpdateCompanyDetails → Open corporate dashboard
                    if (
                      isInUpdateCompanyDetailsContext &&
                      isCorporateType &&
                      (hasChanges || isChild)
                    ) {
                      handleEdit({
                        ...row,
                        mode: "editing",
                        isViewOnly: false,
                        _showCorporateDashboard: true,
                      });
                      return;
                    }

                    // Always use original handleEdit for corporate, joint shareholders
                    if (isCorporateShareholder || isJointShareholder) {
                      handleEdit({
                        ...row,
                        mode: "editing",
                        isViewOnly:
                          (disableEditBtn || row.original?.isReadOnly) &&
                          !isParentComingFromCOTab,
                      });
                      return;
                    }

                    if (needsCompanyOfficialsRedirect) {
                      handleEdit({
                        ...row,
                        mode: "editing",
                        isViewOnly:
                          (disableEditBtn || row.original?.isReadOnly) &&
                          !isParentComingFromCOTab,
                      });
                      return;
                    }

                    // NEW LOGIC: For individuals in UpdateCompanyDetails context
                    if (isInUpdateCompanyDetailsContext) {
                      // If individual needs review (child of changed parent), expand SubRow for editing
                      if (isChild) {
                        if (!row.isExpanded && row?.getToggleRowExpandedProps) {
                          row.getToggleRowExpandedProps().onClick();
                        }
                      } else {
                        // Individual with direct changes - open modal
                        handleEdit({
                          ...row,
                          mode: "editing",
                          isViewOnly: false,
                        });
                      }
                      return;
                    }

                    // Default behavior
                    handleEdit({
                      ...row,
                      mode: "editing",
                      isViewOnly:
                        (disableEditBtn || row.original?.isReadOnly) &&
                        !isParentComingFromCOTab,
                    });
                  }}
                  handleView={(row) => {
                    const isCorporateShareholder =
                      row.original.shareholderWithBO && !row.original.JSGroupID;
                    const isJointShareholder =
                      row.original.shareholderWithBO &&
                      row.original.JSGroupID &&
                      !row.original.linkToCompany;
                    const needsCompanyOfficialsRedirect =
                      !row.original.linkToCompany &&
                      !isInUpdateCompanyDetailsContext;

                    if (isCorporateShareholder || isJointShareholder) {
                      handleEdit({
                        ...row,
                        isViewOnly:
                          (disableEditBtn || row.original?.isReadOnly) &&
                          !isParentComingFromCOTab,
                      });
                    } else if (needsCompanyOfficialsRedirect) {
                      handleEdit({
                        ...row,
                        isViewOnly:
                          (disableEditBtn || row.original?.isReadOnly) &&
                          !isParentComingFromCOTab,
                      });
                    } else if (isInUpdateCompanyDetailsContext) {
                      if (!row.isExpanded && row?.getToggleRowExpandedProps) {
                        row.getToggleRowExpandedProps().onClick();
                      }
                    } else {
                      handleEdit({
                        ...row,
                        isViewOnly:
                          (disableEditBtn || row.original?.isReadOnly) &&
                          !isParentComingFromCOTab,
                      });
                    }
                  }}
                  handleDelete={handleDelete}
                  handleReview={handleReview}
                />
              )}
            {groupBOEntries &&
              !row.isExpanded &&
              row.original?.children &&
              row.original?.children?.length > 1 && (
                <button
                  type="button"
                  {...reviewBtnProps}
                  ref={collapseBtnRef}
                  className={`mt-32 text-gray-900 bg-gray-200 rounded-full items-center p-2`}
                >
                  <PlusIcon className="w-6 h-6" aria-hidden="true" />
                </button>
              )}
          </div>
        );
      },
    },
  ].filter(Boolean);

  const handleReview = async (row) => {
    if (!row.linkToCompany) {
      handleBackBtn();
      await delay();
      let companyShareholderReviewBtn = null;
      if (row?.JSGroupID)
        companyShareholderReviewBtn = document.querySelector(
          `[id="${row?.JSGroupID}_reviewButton"]`
        );
      else if (row?._id && !row.JSGroupID)
        companyShareholderReviewBtn = document.querySelector(
          `[id="${row?._id}_reviewButton"]`
        );
      if (companyShareholderReviewBtn) companyShareholderReviewBtn.click();
      return;
    }
    row?.getToggleRowExpandedProps()?.onClick();
  };

  const renderRowSubComponent = React.useCallback(
    ({ row, rowProps, visibleColumns }) => {
      return (
        <BeneficialOwnersSubRowAsync
          row={row}
          rowProps={rowProps}
          visibleColumns={visibleColumns}
          currentTab="beneficialOwnership"
          handleEdit={handleEdit}
          handleDelete={handleDelete}
          handleBackBtn={handleBackBtn}
          selectedTableType={selectedTableType}
          isInUpdateCompanyDetailsContext={isInUpdateCompanyDetailsContext}
        />
      );
    },
    [isInUpdateCompanyDetailsContext]
  );

  const getRowStyle = (row) => {
    const hasChanges =
      row.original?._hasTransactionChanges || row.original?._isChangedParent;
    const isChild =
      row.original?._isChildOfChangedParent || row.original?._needsReview;

    let bgColor = "bg-white";
    let borderColor = !groupBOEntries
      ? "border-white border-b-0"
      : "border-b border-t border-gray-200";

    if (hasChanges) {
      bgColor = "bg-yellow-50 border-l-4 border-yellow-400";
    } else if (isChild) {
      const parentInfo = getParentInfo(row.original);
      if (parentInfo?.isDecrease) {
        bgColor = "bg-orange-50 border-l-4 border-orange-300";
      } else {
        bgColor = "bg-blue-50 border-l-4 border-blue-300";
      }
    }

    return `h-12 text-sm text-gray-700 ${bgColor} ${borderColor}`;
  };

  const changedCount = data.filter(
    (row) => row._hasTransactionChanges || row._isChangedParent
  ).length;
  const childrenCount = data.filter(
    (row) => row._isChildOfChangedParent || row._needsReview
  ).length;
  const hasAnyChanges = changedCount > 0 || childrenCount > 0;

  return (
    <>
      <NotificationBanner notificationKey="flashMessageBOTable" />
      <div className="w-full bg-white shadow">
        <div className="w-full overflow-x-auto">
          <ReactTable
            columns={columns}
            data={data}
            dataRowStyle={getRowStyle}
            dataRowCellStyle="whitespace-normal px-4"
            renderRowSubComponent={renderRowSubComponent}
            hideColumnsRow={hideColumnsRow}
          />
        </div>
      </div>
    </>
  );
};

export default BeneficialOwnersTable;
